/**
 * Cursor pagination for getWithJourney: page number → API pageToken needed to load that page.
 * Page 1 is always `{ 1: null }`.
 */
export type PageTokenMap = Record<number, string | null>;

export type WithJourneyPageResponse = {
  users?: unknown[];
  nextPageToken?: string | null;
  total?: number | null;
};

/** Single page fetch (organizationId is usually closed over by the caller). */
export type FetchWithJourneyPage = (params: {
  pageSize: number;
  pageToken: string | null;
}) => Promise<WithJourneyPageResponse>;

export type LoadWithJourneyPageResult =
  | { status: 'success'; users: unknown[]; total: number }
  | { status: 'empty'; total: number }
  | { status: 'cancelled' };

/**
 * Fallback total when the API does not return `total` (Ant Design Pagination).
 */
export function estimateTotalWithoutServerCount(
  page: number,
  pageSize: number,
  usersOnPage: number,
  hasNextPage: boolean,
): number {
  return (page - 1) * pageSize + usersOnPage + (hasNextPage ? pageSize : 0);
}

export type FetchWithJourneyPageParams = {
  map: PageTokenMap;
  page: number;
  pageSize: number;
  fetchPage: FetchWithJourneyPage;
  isCancelled: () => boolean;
};

/**
 * Loads one page of users with journey, updating `map` with the next cursor.
 */
export async function fetchWithJourneyPage(
  params: FetchWithJourneyPageParams,
): Promise<LoadWithJourneyPageResult> {
  const { map, page, pageSize, fetchPage, isCancelled } = params;

  const token = await getInputTokenForPage(map, page, pageSize, fetchPage, isCancelled);
  if (isCancelled()) return { status: 'cancelled' };

  if (page > 1 && token === undefined) return { status: 'empty', total: (page - 1) * pageSize };

  const result = await fetchPage({ pageSize, pageToken: token ?? null });
  if (isCancelled()) return { status: 'cancelled' };

  const users = result.users ?? [];
  recordNextPageToken(map, page, result.nextPageToken);

  const total =
    result.total != null
      ? result.total
      : estimateTotalWithoutServerCount(page, pageSize, users.length, !!result.nextPageToken);

  return { status: 'success', users, total };
}

export function createPageTokenMap(): PageTokenMap {
  return { 1: null };
}

/**
 * After a successful page load, store the cursor for the following page (or remove it).
 */
export function recordNextPageToken(
  map: PageTokenMap,
  loadedPage: number,
  nextPageToken: string | null | undefined,
): void {
  if (nextPageToken) map[loadedPage + 1] = nextPageToken;
  else delete map[loadedPage + 1];
}

/**
 * Returns the pageToken to request `targetPage`, using the map or prefetching previous pages.
 */
export async function getInputTokenForPage(
  map: PageTokenMap,
  targetPage: number,
  pageSize: number,
  fetchPage: FetchWithJourneyPage,
  isCancelled: () => boolean,
): Promise<string | null | undefined> {
  if (Object.prototype.hasOwnProperty.call(map, targetPage)) return map[targetPage];

  let cursor: string | null = null;
  for (let i = 1; i < targetPage; i++) {
    const r = await fetchPage({ pageSize, pageToken: cursor });
    if (isCancelled()) return undefined;
    cursor = r.nextPageToken ?? null;
    if (cursor) map[i + 1] = cursor;
    if (!cursor) break;
  }

  return map[targetPage];
}
